/**
 * DashboardTips — lightweight tooltip-style tips shown on first login.
 * Company users see: "Invite Your Team" → "Add Your First Listing"
 * Broker users see: "Add Your First Listing" only.
 * Tips show one at a time. Dismissed via localStorage.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface Tip {
  id: string;
  i18nKey: string;
  targetSelector: string;
}

const COMPANY_TIPS: Tip[] = [
  { id: 'inviteTeam', i18nKey: 'tips.inviteTeam', targetSelector: '[data-tip-target="team"]' },
  { id: 'addListing', i18nKey: 'tips.addListing', targetSelector: '[data-tip-target="properties"]' },
];

const BROKER_TIPS: Tip[] = [
  { id: 'addListing', i18nKey: 'tips.addListing', targetSelector: '[data-tip-target="add-listing"]' },
];

const STORAGE_KEY = 'safaqat24_tips_dismissed';

interface DashboardTipsProps {
  accountType: 'company' | 'broker';
}

export function DashboardTips({ accountType }: DashboardTipsProps) {
  const { t, i18n } = useTranslation('onboarding');
  const isRTL = i18n.language === 'ar';
  const tips = accountType === 'company' ? COMPANY_TIPS : BROKER_TIPS;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(true);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Check if tips were already dismissed
  useEffect(() => {
    const wasDismissed = localStorage.getItem(STORAGE_KEY) === 'true';
    setDismissed(wasDismissed);

    // Clean up old onboarding wizard localStorage keys (one-time migration)
    localStorage.removeItem('safaqat24.onboarding.progress');
    localStorage.removeItem('safaqat24.onboarding.deferred');
  }, []);

  // Position the tooltip relative to target element
  const updatePosition = useCallback(() => {
    if (dismissed || currentIndex >= tips.length) return;

    const target = document.querySelector(tips[currentIndex].targetSelector);
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const scrollTop = window.scrollY;

    setPosition({
      top: rect.bottom + scrollTop + 8,
      left: rect.left + rect.width / 2,
    });
  }, [dismissed, currentIndex, tips]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [updatePosition]);

  // Also reposition after a short delay to wait for dashboard to render
  useEffect(() => {
    const timer = setTimeout(updatePosition, 500);
    return () => clearTimeout(timer);
  }, [updatePosition]);

  const dismissAll = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setDismissed(true);
  }, []);

  const handleGotIt = useCallback(() => {
    if (currentIndex < tips.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      dismissAll();
    }
  }, [currentIndex, tips.length, dismissAll]);

  if (dismissed || currentIndex >= tips.length || !position) return null;

  const tip = tips[currentIndex];

  return (
    <>
      {/* Subtle backdrop */}
      <div className="fixed inset-0 z-40 bg-black/10" onClick={dismissAll} />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-50 w-72 rounded-xl bg-white shadow-lg border border-gray-200 p-4"
        style={{
          top: position.top,
          left: position.left,
          transform: 'translateX(-50%)',
        }}
      >
        {/* Arrow pointing up */}
        <div
          className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45 bg-white border-l border-t border-gray-200"
        />

        <div dir={isRTL ? 'rtl' : 'ltr'}>
          <h3 className="font-semibold text-gray-900 text-sm mb-1">
            {t(`${tip.i18nKey}.title`)}
          </h3>
          <p className="text-gray-500 text-xs mb-3">
            {t(`${tip.i18nKey}.description`)}
          </p>

          <div className="flex items-center justify-between">
            <button
              onClick={dismissAll}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              {t('tips.skipAll')}
            </button>
            <button
              onClick={handleGotIt}
              className="px-3 py-1.5 bg-[#004D40] text-white text-xs font-medium rounded-lg hover:bg-[#003930] transition-colors"
            >
              {t('tips.gotIt')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
